import React, { Component } from 'react';
import { Bert } from 'meteor/themeteorchef:bert';
import turf from '@turf/turf';
import { Form, Button, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { isEmpty, assign, includes, map } from 'lodash';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import * as layerOps from '../../../api/Layers/layer-operations';
import methodPromise from '../../../modules/method-promise-wrapper';
import Layers, {
  LAYER_SUBTYPES,
  LAYER_TYPES,
  GEOJSON_GEOMETRIES
} from '../../../api/Layers/Layers';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import adaptURL from '../../../modules/adapt-url';
import { coordinatesToXYZ } from '../../../modules/coordinates-to-xyz';
import loadGeoJSONFromURL from '../../../modules/load-geojson-from-url';
import loadMapBoxLayerNames from '../../../modules/load-mapbox-layer-names';
import { parseGeoJSONProperties } from '../../../modules/parse-geojson-properties';
import getGeoJSONURLFromMapbox from '../../../modules/get-geojson-url-from-mapbox';
import getGeoJSONFromMapBox from '../../../modules/get-geojson-from-mapbox';
import LayerAttributionField from '../../atoms/LayerAttributionField';
import Validation from '../../containers/Validation';
import LayerSubdomainField from '../../atoms/LayerSubdomainField';
import LayerZoomField from '../../atoms/LayerZoomField';
import Loading from '../Loading';
import LoadButton from '../../atoms/LoadButton';

const NEGATIVE_Y_PARAM_REGEX = new RegExp(/{-y}/);

class LayerLoad extends Component {
  state = {
    geojsonLoading: false,
    geojsonLoaded: true,
    isExternal: false,
    isModal: false,
    layerGeometry: layerOps.getGeoJSONGeometry(this.props.layer),
    layerType: layerOps.getType(this.props.layer),
    layerSubType: layerOps.getSubType(this.props.layer),
    TileParams: { x: 0, y: 0, z: 0 },
    mapboxLayerNames: [],
    metadataLayer: isEmpty(this.props.history.location.state)
      ? {}
      : this.props.history.location.state.metadata,
    mapboxLayerName: '',
    selectedLayer: ''
  };

  componentDidMount() {
    const { history } = this.props;
    const dat = localStorage.getItem('Metadata');
    const data = JSON.parse(dat);
    if (isEmpty(history.location.state)) {
      if (isEmpty(data)) {
        this.redirectToDB();
      } else {
        this.setState({ metadataLayer: data, isModal: data.isModal });
        localStorage.removeItem('Metadata');
      }
    }
  }

  handleSubmit = async form => {
    const { layer } = this.props;
    const {
      geojsonProps,
      isModal,
      layerGeometry,
      layerSubType,
      layerType,
      metadataLayer
    } = this.state;
    const incrX = (metadataLayer.bounds[2] - metadataLayer.bounds[0]) / 16;
    const incrY = (metadataLayer.bounds[3] - metadataLayer.bounds[1]) / 16;
    const maxDiff = incrX > incrY ? incrX : incrY;
    const zoomLevel = Math.round(
      -1 * (Math.log(maxDiff * 16) / Math.log(2) - Math.log(360) / Math.log(2))
    );
    const MapLocation = {
      type: 'Feature',
      properties: {
        zoom: zoomLevel
      },
      geometry: {
        type: 'Point',
        coordinates: metadataLayer.center
      },
      bbox: metadataLayer.bounds
    };
    const existingLayer = layerOps.getId(layer);
    const methodToCall = !isEmpty(existingLayer) ? 'layers.update' : 'layers.insert';

    const newLayer = {
      name: form.name.value.trim(),
      description: form.description.value.trim(),
      layerType,
      layerSubType
    };
    if (!layerOps.isThematic(newLayer))
      assign(newLayer, {
        imageUrl: form.imageUrl.value.trim(),
        maxZoom: Number(form.maxZoom.value.trim()),
        minZoom: Number(form.minZoom.value.trim())
      });
    if (layerOps.isSpatial(newLayer))
      assign(newLayer, {
        attribution: form.attribution.value.trim(),
        mapLocation: MapLocation
      });

    if (layerOps.isMapBox(newLayer)) {
      if (form.mapboxLayerNameText)
        assign(newLayer, {
          url: form.url.value.trim(),
          mapboxLayerName: form.mapboxLayerNameText.value.trim(),
          mapboxProps: [],
          geojsonGeometry: layerGeometry,
          subdomains: form.subdomains.value.trim()
        });
      else
        assign(newLayer, {
          url: form.url.value.trim(),
          mapboxLayerName: form.mapboxLayerNameSelect.value.trim(),
          mapboxProps: geojsonProps,
          geojsonGeometry: layerGeometry,
          subdomains: form.subdomains.value.trim()
        });
    }
    if (!isEmpty(existingLayer)) newLayer._id = existingLayer;
    try {
      const layerId = await methodPromise(methodToCall, newLayer);
      if (isModal) {
        this.props.setIpLayer(layerId);
      } else {
        this.redirectToLayerView(form, existingLayer, layerId);
      }
    } catch (error) {
      handleErrorInClient(error);
    }
    return true;
  };

  loadMapBoxLayersOrGeoJSON = async () => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const { metadataLayer } = this.state;
    try {
      const mapboxUrl = adaptURL(metadataLayer.tileurl);
      const { adaptedUrl, layerName } = getGeoJSONURLFromMapbox(mapboxUrl);
      let geojson;
      if (!isEmpty(layerName)) {
        geojson = await loadGeoJSONFromURL(adaptedUrl);
        const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson);
        this.setState({
          geojsonProps: geojsonProperties,
          mapboxLayerName: layerName,
          layerGeometry: geojsonGeometries[0],
          isExternal: false,
          geojsonLoading: false,
          geojsonLoaded: true
        });
        Bert.alert('File Loaded', 'success');
      } else {
        const { layer } = this.props;
        const zl = Math.round((metadataLayer.maxzoom + metadataLayer.minzoom) / 2);
        const defaultMapCenter = isEmpty(layerOps.getMapLocation(layer))
          ? turf.point([metadataLayer.center[0], metadataLayer.center[1]], { zoom: zl })
          : layerOps.getMapLocation(layer);
        const layerTileParams = coordinatesToXYZ(defaultMapCenter);
        const mapboxLayerNames = await loadMapBoxLayerNames(adaptedUrl, layerTileParams);
        if (mapboxLayerNames.length < 1) {
          const defaultMapCenter2 = isEmpty(layerOps.getMapLocation(layer))
            ? turf.point([0, 0], { zoom: 0 })
            : layerOps.getMapLocation(layer);
          const layerTileParams2 = coordinatesToXYZ(defaultMapCenter2);

          layerTileParams2.x = 0;
          layerTileParams2.z = 0;
          if (adaptedUrl.match(NEGATIVE_Y_PARAM_REGEX)) layerTileParams2['-y'] = 0;
          else layerTileParams2.y = 0;

          const mapboxLayerNames2 = await loadMapBoxLayerNames(adaptedUrl, layerTileParams2);

          if (mapboxLayerNames2.length < 1) {
            let mapboxLayerNames3 = [];
            let i = 0;
            let layerTileParams3 = {};
            do {
              const incrX = (metadataLayer.bounds[2] - metadataLayer.bounds[0]) / 16;
              const incrY = (metadataLayer.bounds[3] - metadataLayer.bounds[1]) / 16;
              const RamdX = metadataLayer.bounds[2] - incrX * i;
              const RamdY = metadataLayer.bounds[3] - incrY * i;
              const maxDiff = incrX > incrY ? incrX : incrY;
              const zoomLevel = Math.round(
                -1 * (Math.log(maxDiff) / Math.log(2) - Math.log(360) / Math.log(2))
              );
              const defaultMapCenter3 = isEmpty(layerOps.getMapLocation(layer))
                ? turf.point([RamdX, RamdY], { zoom: zoomLevel })
                : layerOps.getMapLocation(layer);
              layerTileParams3 = coordinatesToXYZ(defaultMapCenter3);
              // eslint-disable-next-line no-await-in-loop
              mapboxLayerNames3 = await loadMapBoxLayerNames(adaptedUrl, layerTileParams3);
              i += 1;
            } while (mapboxLayerNames3.length < 1);
            if (mapboxLayerNames3.length > 0) {
              this.setState({
                geojsonLoading: false,
                isExternal: true,
                TileParams: layerTileParams3,
                mapboxLayerName: '',
                mapboxLayerNames: mapboxLayerNames3
              });
            }
          } else {
            this.setState({
              geojsonLoading: false,
              isExternal: true,
              TileParams: layerTileParams2,
              mapboxLayerName: '',
              mapboxLayerNames: mapboxLayerNames2
            });
          }
        } else {
          this.setState({
            geojsonLoading: false,
            isExternal: true,
            TileParams: layerTileParams,
            mapboxLayerName: '',
            mapboxLayerNames
          });
        }
      }
    } catch (err) {
      this.setState({
        geojsonProps: [],
        mapboxLayerName: '',
        mapboxLayerNames: [],
        geojsonLoading: false,
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadMapBoxFromLayerName = async event => {
    if (!isEmpty(event.target.value.trim())) {
      this.setState({ geojsonLoading: true, geojsonLoaded: false });
      try {
        const mapboxUrl = adaptURL(event.target.form.url.value.trim());
        const mapboxLayerName = event.target.value.trim();
        const { adaptedUrl } = getGeoJSONURLFromMapbox(mapboxUrl);
        const layerTileParams = {};
        if (event.target.form.mapboxLayerX) {
          layerTileParams.x = event.target.form.mapboxLayerX.value.trim();
          layerTileParams.z = event.target.form.mapboxLayerZ.value.trim();
          if (adaptedUrl.match(NEGATIVE_Y_PARAM_REGEX))
            layerTileParams['-y'] = event.target.form.mapboxLayerY.value.trim();
          else layerTileParams.y = event.target.form.mapboxLayerY.value.trim();
        }
        const mapboxGeoJSON = await getGeoJSONFromMapBox({
          layerTileParams,
          mapboxLayerName,
          url: adaptedUrl
        });
        const { geojsonGeometries, geojsonProperties } = parseGeoJSONProperties(mapboxGeoJSON);
        this.setState({
          geojsonLoaded: true,
          geojsonLoading: false,
          geojsonProps: geojsonProperties,
          layerGeometry: geojsonGeometries[0],
          mapboxLayerName
        });
        Bert.alert('File Loaded', 'success');
      } catch (err) {
        this.setState({
          geojsonProps: [],
          mapboxLayerName: '',
          geojsonLoading: false,
          geojsonLoaded: false,
          layerGeometry: GEOJSON_GEOMETRIES.POLYGON
        });
        console.error(err);
        handleErrorInClient(err);
      }
    } else {
      this.setState({
        geojsonProps: [],
        mapboxLayerName: '',
        geojsonLoading: false,
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      Bert.alert('Please select a Layer Name', 'warning');
    }
  };

  selectLayer = event => {
    const e = event.target.value.trim();
    this.setState({ selectedLayer: e });
    this.loadMapBoxFromLayerName(event);
  };

  redirectToLayerView(form, existingLayer, layerId) {
    const { history } = this.props;
    const confirmation = !isEmpty(existingLayer) ? 'Layer updated!' : 'Layer added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/layers/${layerId}`);
  }

  redirectToDB() {
    const { history } = this.props;
    history.push('/DBLayerServers');
  }

  render() {
    const { layer, isFree, unfilteredCount, isBasic, deployConfig } = this.props;
    const {
      geojsonLoading,
      mapboxLayerName,
      mapboxLayerNames,
      selectedLayer,
      TileParams,
      layerGeometry,
      layerSubType,
      metadataLayer,
      layerType
    } = this.state;
    const isNewLayer = isEmpty(layerOps.getId(layer));
    const validationProps = {
      rules: {
        name: {
          required: true
        },
        description: {
          required: false
        },
        layerType: {
          required: true
        },
        layerSubType: {
          required: true
        },
        layerNames: {
          required: false
        },
        url: {
          required: includes([LAYER_SUBTYPES.MAPBOX], layerSubType)
        },
        imageUrl: {
          required: false
        },
        maxZoom: {
          required: false
        },
        minZoom: {
          requires: false
        },
        attribution: {
          required: layerType === LAYER_TYPES.SPATIAL
        },
        subdomains: {
          required: false
        },
        mapboxLayerName: {
          required: layerSubType === LAYER_SUBTYPES.MAPBOX
        },
        mapboxPropertyName: {
          required: layerSubType === LAYER_SUBTYPES.MAPBOX
        },
        mapServerElementType: {
          required: layerSubType === LAYER_SUBTYPES.MAPBOX
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        },
        layerType: {
          required: 'This needs a layer type.'
        },
        layerSubType: {
          required: 'This needs a layer subtype.'
        },
        url: {
          required: 'You need to provide the URL'
        },
        attribution: {
          required: 'You need to provide attribution for a tiled server layer'
        },
        mapboxLayerName: {
          required: 'You need a layer name for the MapBox'
        },
        mapboxPropertyName: {
          required: 'You need a property name for the MapBox'
        },
        mapServerElementType: {
          required: 'You need to select the type of the property'
        }
      },
      submitHandler: form => {
        if (!isFree && !isBasic) {
          this.handleSubmit(form);
        } else if (isFree && unfilteredCount < deployConfig.Roles.Free.LayersLimit) {
          this.handleSubmit(form);
        } else if (isBasic && unfilteredCount < deployConfig.Roles.Basic.LayersLimit) {
          this.handleSubmit(form);
        } else {
          Bert.alert('You need to upgrade your plan to add more Layers', 'info');
        }
      }
    };
    if (isEmpty(metadataLayer)) {
      return <Loading />;
    }
    return (
      <Validation {...validationProps}>
        <Form onSubmit={event => event.preventDefault()}>
          <Row>
            <Col xs={12} md={6} lg={4}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  defaultValue={layerOps.getName(layer)}
                  placeholder="Oh, The Places You'll Go!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  defaultValue={layerOps.getDescription(layer)}
                  placeholder="Congratulations! Today is your day. You're off to Great Places! You're
                off and away!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Layer Type</Form.Label>
                <Form.Control
                  type="text"
                  name="imageUrl"
                  disabled
                  defaultValue="Spatial Layer"
                  placeholder="An image to use as preview"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Layer Subtype</Form.Label>
                <Form.Control
                  type="text"
                  name="imageUrl"
                  disabled
                  defaultValue="Mapbox"
                  placeholder="An image to use as preview"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label> URL</Form.Label>
                <Form.Row>
                  <Col xs={10}>
                    <Form.Control
                      type="text"
                      name="url"
                      ref={this.urlInput}
                      defaultValue={metadataLayer.tileurl}
                      placeholder="Where is your layer?"
                    />
                  </Col>

                  <Col xs={2}>
                    <LoadButton
                      isLoading={geojsonLoading}
                      clickAction={this.loadMapBoxLayersOrGeoJSON}
                    />
                  </Col>
                </Form.Row>
              </Form.Group>
              <Form.Group>
                <Form.Label>Layer Name</Form.Label>
                <Form.Control
                  as="select"
                  name="mapboxLayerNameSelect"
                  value={mapboxLayerName}
                  onChange={this.selectLayer}
                >
                  <option value="">Other Layer Name</option>;
                  {map(mapboxLayerNames, layerName => {
                    return (
                      <option value={layerName} key={layerName}>
                        {layerName}
                      </option>
                    );
                  })}
                </Form.Control>
                {isEmpty(selectedLayer) && (
                  <Form.Control
                    name="mapboxLayerNameText"
                    placeholder="Write a different Layer Name"
                    defaultValue=""
                  />
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label>Layer Geometry</Form.Label>
                <Form.Control
                  as="select"
                  name="mapboxGeometry"
                  value={layerGeometry}
                  onChange={event => this.setState({ layerGeometry: event.target.value.trim() })}
                >
                  {map(GEOJSON_GEOMETRIES, geometry => {
                    return (
                      <option value={geometry} key={geometry}>
                        {geometry}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
              <Form.Group as={Row}>
                <Form.Label column xs={6} sm={1}>
                  X
                </Form.Label>
                <Col xs={6} sm={3}>
                  <Form.Control type="number" name="mapboxLayerX" value={TileParams.x} readOnly />
                </Col>
                <Form.Label column xs={6} sm={1}>
                  Y
                </Form.Label>
                <Col xs={6} sm={3}>
                  <Form.Control type="number" name="mapboxLayerY" value={TileParams.y} readOnly />
                </Col>
                <Form.Label column xs={6} sm={1}>
                  Z
                </Form.Label>
                <Col xs={6} sm={3}>
                  <Form.Control type="number" name="mapboxLayerZ" value={TileParams.z} readOnly />
                </Col>
              </Form.Group>
              <LayerSubdomainField {...this.props} {...this.state} />
              <LayerZoomField {...this.props} {...this.state} />
              <Form.Group>
                <Form.Label>Image Preview</Form.Label>
                <Form.Control
                  type="text"
                  name="imageUrl"
                  defaultValue=""
                  placeholder="An image to use as preview"
                />
              </Form.Group>
              <LayerAttributionField {...this.props} {...this.state} />
            </Col>
          </Row>
          <br />
          <Button type="submit" variant="outline-success" disabled={false}>
            {isNewLayer ? 'Add Layer' : 'Save Changes'}
          </Button>
        </Form>
      </Validation>
    );
  }
}
LayerLoad.defaultProps = {
  layer: {
    name: '',
    description: '',
    layerType: LAYER_TYPES.SPATIAL,
    layerSubType: LAYER_SUBTYPES.MAPBOX
  },
  setIpLayer: () => {}
};

LayerLoad.propTypes = {
  layer: PropTypes.object,
  history: PropTypes.object.isRequired,
  setIpLayer: PropTypes.func,
  isFree: PropTypes.bool.isRequired,
  isBasic: PropTypes.bool.isRequired,
  unfilteredCount: PropTypes.number.isRequired,
  deployConfig: PropTypes.object.isRequired
};

export default withTracker(() => {
  const layersSub = Meteor.subscribe('layers');

  return {
    loading: !layersSub.ready(),
    unfilteredCount: Layers.find({ deleted: { $eq: 'no' } }).count()
  };
})(LayerLoad);
